import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    course: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCourseChange = (value) => {
    setFormData({
      ...formData,
      course: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    if (!formData.name || !formData.phone || !formData.course) {
      toast.error('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    // Simulate form submission
    setTimeout(() => {
      // Send to WhatsApp
      const whatsappMessage = `New Enquiry from ZOOM Academy Website:\n\nName: ${formData.name}\nPhone: ${formData.phone}\nAddress: ${formData.address}\nCourse: ${formData.course}\nMessage: ${formData.message}`;
      const whatsappUrl = `https://wa.me/919991129789?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');

      toast.success('Thank you! We will contact you soon.');
      setFormData({
        name: '',
        phone: '',
        address: '',
        course: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get In <span className="text-blue-600">Touch</span>
          </h2>
          <div className="w-24 h-1 bg-yellow-400 mx-auto mb-6"></div>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Ready to start your journey? Contact us for admission details
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Admission Enquiry Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Admission Enquiry Form</h3>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Student Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address
                </label>
                <Textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your address"
                  rows={3}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Interested Course *
                </label>
                <Select value={formData.course} onValueChange={handleCourseChange} required>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bsc-agriculture">B.Sc Agriculture (CCS HAU)</SelectItem>
                    <SelectItem value="bsc-fisheries">B.Sc Fisheries</SelectItem>
                    <SelectItem value="bsc-nursing">B.Sc Nursing</SelectItem>
                    <SelectItem value="vlda">VLDA (LUVAS)</SelectItem>
                    <SelectItem value="cuet">11th & 12th (CUET)</SelectItem>
                    <SelectItem value="govt-exams">Government Exams</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Any specific questions or requirements?"
                  rows={4}
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 sm:py-6 text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="mr-2 w-5 h-5" />
                    Get a Call Back from Our Counselors
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Contact Cards */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 sm:p-8 text-white">
              <h3 className="text-xl sm:text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Phone Numbers</p>
                    <a href="tel:9991129789" className="block text-blue-100 hover:text-white transition-colors text-sm sm:text-base">
                      99911-29789
                    </a>
                    <a href="tel:8607029789" className="block text-blue-100 hover:text-white transition-colors text-sm sm:text-base">
                      86070-29789
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Email</p>
                    <a href="mailto:info@zoomacademy.com" className="text-blue-100 hover:text-white transition-colors text-sm sm:text-base break-all">
                      info@zoomacademy.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Address</p>
                    <p className="text-blue-100 text-sm sm:text-base">
                      Gali No. 2, Rishi Nagar<br />
                      Near Bus Stand<br />
                      Hisar – 125001, Haryana
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=ZOOM+Academy,+Gali+No.+2,+Rishi+Nagar,+Near+Bus+Stand,+Hisar,+Haryana+125001"
                target="_blank"
                rel="noopener noreferrer"
                className="block relative group cursor-pointer"
                data-testid="map-directions-link"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3490.8!2d75.7217!3d29.1534!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391232c1c4e5a5a5%3A0x1234567890abcdef!2sRishi%20Nagar%2C%20Hisar%2C%20Haryana%20125001!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="200"
                  style={{ border: 0, pointerEvents: 'none' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="ZOOM Academy Location"
                  className="sm:h-[250px]"
                ></iframe>
                {/* Overlay with Get Directions button */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <div className="bg-blue-600 text-white px-6 py-3 rounded-full font-bold flex items-center gap-2 shadow-lg opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <MapPin className="w-5 h-5" />
                    Get Directions
                  </div>
                </div>
              </a>
            </div>

            {/* Office Hours */}
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl p-6 sm:p-8 text-gray-900">
              <h3 className="text-xl sm:text-2xl font-bold mb-4">Office Hours</h3>
              <div className="space-y-2 text-sm sm:text-base">
                <div className="flex justify-between">
                  <span className="font-semibold">Monday - Saturday:</span>
                  <span>9:00 AM - 7:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Sunday:</span>
                  <span>10:00 AM - 5:00 PM</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
